"use client";

// Packages
import React, { useEffect, useRef, ReactNode, useId } from "react";
import classNames from "classnames";
import { FocusTrap } from 'focus-trap-react';

// Styles
import styles from "./modal.module.scss";

type ModalProps = {
  content: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  wrapperClass?: string;
  modalClass?: string;
  modalExtensionClass?: string;
  enableCloseButton?: boolean;
  enableDismiss?: boolean;
  closeClass?: string;
};

const Modal = ({
  content,
  isOpen,
  onClose,
  ariaLabel = "Dialog",
  ariaLabelledBy,
  ariaDescribedBy,
  wrapperClass = "",
  modalClass = "",
  modalExtensionClass = "",
  enableCloseButton = true,
  enableDismiss = true,
  closeClass = "",
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const generatedDescId = useId();
  const describedBy = ariaDescribedBy || generatedDescId;

  useEffect(() => {
    if (isOpen && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
        'a, button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), details, [tabindex]:not([tabindex="-1"])'
      );
      const firstFocusableElement = focusableElements[0];
      if (firstFocusableElement) {
        firstFocusableElement.focus();
      } else {
        modalRef.current.focus();
      }

      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape" && enableDismiss) {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener("keydown", handleEsc);
    }
    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [isOpen, onClose, enableDismiss]);

  const closeModal = () => {
    if (enableDismiss) {
      onClose();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (enableDismiss && e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleBackdropKeyDown = (e: React.KeyboardEvent) => {
    if (e.target !== e.currentTarget) {
      return;
    }
    if (enableDismiss && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onClose();
    }
  };

  const hasLabelledBy = !!ariaLabelledBy;

  return isOpen ? (
    <FocusTrap focusTrapOptions={{
      clickOutsideDeactivates: enableDismiss,
      escapeDeactivates: false,
    }}
    >
      <div
        className={classNames(styles.wrapper, wrapperClass)}
        onClick={enableDismiss ? handleBackdropClick : undefined}
        onKeyDown={enableDismiss ? handleBackdropKeyDown : undefined}
        role={enableDismiss ? "button" : undefined}
        tabIndex={enableDismiss ? 0 : undefined}
        aria-label={enableDismiss ? "Close modal" : undefined}
      >
        <div
          ref={modalRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby={hasLabelledBy ? ariaLabelledBy : undefined}
          aria-label={!hasLabelledBy ? ariaLabel : undefined}
          aria-describedby={describedBy}
          className={classNames(styles.modal, modalClass, modalExtensionClass)}
        >
          {!ariaDescribedBy && (
            <p id={generatedDescId} className="screenReaderText">
              Dialog content
            </p>
          )}

          {enableCloseButton && (
            <button
              ref={closeButtonRef}
              className={classNames(styles.close, closeClass)}
              onClick={closeModal}
              aria-label="Close modal"
            ></button>
          )}
          {content}
        </div>
      </div>
    </FocusTrap>
  ) : null;
};

export default Modal;
